import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useMetaTracking } from "@/hooks/useMetaTracking";
import ProtectedRoute from "@/components/ProtectedRoute";

// Eager load Index for best LCP
import Index from "./pages/Index";

// Lazy load all other routes to reduce initial bundle
const NotFound = lazy(() => import("./pages/NotFound"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Upsell = lazy(() => import("./pages/Upsell"));
const Upsell2 = lazy(() => import("./pages/Upsell2"));
const Downsell = lazy(() => import("./pages/Downsell"));
const Downsell2 = lazy(() => import("./pages/Downsell2"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Auth = lazy(() => import("./pages/Auth"));
const FunnelAnalytics = lazy(() => import("./pages/FunnelAnalytics"));
const EmailCampaigns = lazy(() => import("./pages/EmailCampaigns"));
const NewEmailCampaign = lazy(() => import("./pages/NewEmailCampaign"));
const DripSequences = lazy(() => import("./pages/DripSequences"));
const ManageSubscription = lazy(() => import("./pages/ManageSubscription"));
const Policies = lazy(() => import("./pages/Policies"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

// Wrapper component to use hooks
const AppRoutes = () => {
  useMetaTracking();
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Index />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/upsell" element={<Upsell />} />
        <Route path="/upsell-2" element={<Upsell2 />} />
        <Route path="/downsell" element={<Downsell />} />
        <Route path="/downsell-2" element={<Downsell2 />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/manage-subscription" element={<ManageSubscription />} />
        <Route path="/policies" element={<Policies />} />
        
        {/* Protected admin routes */}
        <Route path="/analytics" element={
          <ProtectedRoute requireAdmin>
            <FunnelAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/email-campaigns" element={
          <ProtectedRoute requireAdmin>
            <EmailCampaigns />
          </ProtectedRoute>
        } />
        <Route path="/email-campaigns/new" element={
          <ProtectedRoute requireAdmin>
            <NewEmailCampaign />
          </ProtectedRoute>
        } />
        <Route path="/drip-sequences" element={
          <ProtectedRoute requireAdmin>
            <DripSequences />
          </ProtectedRoute>
        } />
        
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
